import { useCallback, useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { ChevronLeft, ExternalLink, Mail, MapPin, MessageCircle, Phone } from 'lucide-react';
import publicCta from '../../content/publicCta.json';
import FormField from '../../components/ui/FormField.jsx';
import Button from '../../components/ui/Button.jsx';
import api from '../../services/api.js';
import { toastError, toastSuccess } from '../../hooks/useToast.js';
import { getPqrsTypeLabel, PQRS_TYPES } from '../../constants/pqrsTypes.js';
import ScrollReveal from '../../components/public/ScrollReveal.jsx';
import SectionAmbientGradient from '../../components/public/SectionAmbientGradient.jsx';
import {
  DEPARTMENT_CONTACT_LINES,
  getDepartmentWhatsAppHref,
  OFFICE_EMAIL,
  OFFICE_LOCATION_LABEL,
  OFFICE_MAPS_EMBED_URL,
  OFFICE_MAPS_URL,
  OFFICE_PHONE_DISPLAY,
} from '../../constants/contact.js';
import {
  formatPhoneDisplay,
  getWhatsAppUrl,
  WHATSAPP_PHONE,
} from '../../constants/whatsappContact.js';
import SumCaptcha from '../../components/auth/SumCaptcha.jsx';

const inputLight =
  'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#1acb12] focus:ring-2 focus:ring-[#1acb12]/25';

const selectLight = `${inputLight} cursor-pointer appearance-none bg-[length:1rem] bg-[right_0.75rem_center] bg-no-repeat pr-10 [background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")]`;

const inputInvalid = 'border-red-400 ring-2 ring-red-200 focus:border-red-500 focus:ring-red-200';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function fieldClass(base, hasError) {
  return hasError ? `${base} ${inputInvalid}` : base;
}

function ContactInfoItem({ icon: Icon, label, children }) {
  return (
    <li className="flex gap-4 rounded-xl border border-slate-100 bg-slate-50/80 p-4">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#1acb12]/12 ring-1 ring-[#1acb12]/25">
        <Icon className="h-5 w-5 text-[#00A63E]" strokeWidth={1.75} aria-hidden />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-bold uppercase tracking-wide text-slate-500">{label}</p>
        <div className="mt-1 text-base font-medium text-slate-800">{children}</div>
      </div>
    </li>
  );
}

export default function ContactPage() {
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaChallenge, setCaptchaChallenge] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaValid, setCaptchaValid] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(true);

  const loadCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    try {
      const { data } = await api.get('/v1/public/contacto/captcha');
      if (data.success && data.data) {
        setCaptchaToken(data.data.token ?? '');
        setCaptchaChallenge({
          a: data.data.a,
          b: data.data.b,
          options: data.data.options ?? [],
        });
      }
    } catch {
      setCaptchaToken('');
      setCaptchaChallenge(null);
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  const { register, handleSubmit, reset, watch, control } = useForm({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      nombre: '',
      email: '',
      telefono: '',
      tipoPqrs: '',
      mensaje: '',
      website: '',
    },
  });

  const { errors, isSubmitting, isSubmitted } = useFormState({ control });

  const tipoPqrs = watch('tipoPqrs');
  const tipoInfo = PQRS_TYPES.find((t) => t.value === tipoPqrs);
  const phoneHref = `tel:${OFFICE_PHONE_DISPLAY.replace(/\D/g, '')}`;
  const contactWaTopic = publicCta.whatsappTopics?.find((t) => t.id === 'contacto');
  const whatsappHref = WHATSAPP_PHONE
    ? getWhatsAppUrl(contactWaTopic?.message || publicCta.whatsappMessage)
    : '';

  function onInvalid() {
    toastError('Revisa el formulario', 'Completa los campos obligatorios marcados en rojo.');
  }

  async function onSubmit(values) {
    if (!captchaValid || !captchaToken) {
      toastError('Verificación requerida', 'Resuelve la suma de seguridad antes de enviar.');
      return;
    }

    try {
      const { data } = await api.post('/v1/public/contacto', {
        nombre: values.nombre.trim(),
        email: values.email.trim(),
        telefono: values.telefono?.trim() ?? '',
        tipo_pqrs: values.tipoPqrs,
        mensaje: values.mensaje.trim(),
        captcha_token: captchaToken,
        captcha_answer: captchaAnswer,
        website: values.website ?? '',
      });
      if (!data.success) throw new Error(data.message);
      const tipoLabel = getPqrsTypeLabel(values.tipoPqrs);
      toastSuccess(
        'PQRS registrada',
        data.message ||
          `Tu ${tipoLabel.toLowerCase()} fue recibida; el área correspondiente dará respuesta en los plazos establecidos.`
      );
      reset();
      setCaptchaAnswer('');
      setCaptchaValid(false);
      await loadCaptcha();
    } catch (e) {
      const fields = e.payload?.errors;
      if (fields && typeof fields === 'object') {
        const first = Object.values(fields)[0];
        toastError('Revisa el formulario', typeof first === 'string' ? first : e.message);
        if (fields.captcha) {
          setCaptchaAnswer('');
          setCaptchaValid(false);
          await loadCaptcha();
        }
      } else {
        toastError('No se pudo enviar', e.message);
        if (e.status === 429) {
          setCaptchaAnswer('');
          setCaptchaValid(false);
        }
      }
    }
  }

  return (
    <div className="pb-20">
      <section className="relative overflow-hidden border-b border-white/10">
        <SectionAmbientGradient />
        <div className="relative mx-auto max-w-7xl px-6 py-14 lg:px-8 lg:py-16">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-base font-semibold text-accent-lime transition hover:text-white"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            Volver al inicio
          </Link>
          <ScrollReveal className="mx-auto mt-10 max-w-3xl text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-accent-lime">PQRS y atención</p>
            <h1 className="mt-4 font-display text-3xl font-bold text-white md:text-4xl lg:text-5xl">
              Peticiones, quejas, reclamos y sugerencias
            </h1>
            <p className="mt-5 text-xl leading-relaxed text-slate-300 md:text-2xl">
              Registra tu solicitud indicando el tipo de PQRS, escríbenos por WhatsApp o comunícate con nosotros en la sede principal.
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-start lg:gap-14">
            <ScrollReveal>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_12px_40px_rgba(13,43,127,0.06)] ring-1 ring-slate-100 lg:p-10">
                <h2 className="font-display text-2xl font-bold text-[#0d2b7f] md:text-3xl">Formulario PQRS</h2>
                <p className="mt-2 text-base text-slate-600">
                  Completa los campos y envía tu solicitud. Te responderemos por los canales de contacto registrados.
                </p>

                <form className="relative mt-8 space-y-6" onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate>
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden
                    className="pointer-events-none absolute h-0 w-0 opacity-0"
                    {...register('website')}
                  />
                  {isSubmitted && Object.keys(errors).length > 0 ? (
                    <p
                      className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-base text-red-700"
                      role="alert"
                    >
                      Hay campos pendientes o con formato incorrecto. Corrígelos antes de enviar.
                    </p>
                  ) : null}

                  <div className="grid gap-6 md:grid-cols-2">
                    <FormField
                      label="Nombre completo"
                      error={errors.nombre?.message}
                      variant="light"
                      size="lg"
                      required
                    >
                      <input
                        className={fieldClass(inputLight, Boolean(errors.nombre))}
                        autoComplete="name"
                        aria-invalid={Boolean(errors.nombre)}
                        {...register('nombre', {
                          required: 'El nombre es obligatorio',
                          minLength: { value: 3, message: 'Mínimo 3 caracteres' },
                          maxLength: { value: 120, message: 'Máximo 120 caracteres' },
                        })}
                      />
                    </FormField>
                    <FormField
                      label="Correo electrónico"
                      error={errors.email?.message}
                      variant="light"
                      size="lg"
                      required
                    >
                      <input
                        type="email"
                        className={fieldClass(inputLight, Boolean(errors.email))}
                        autoComplete="email"
                        aria-invalid={Boolean(errors.email)}
                        {...register('email', {
                          required: 'El correo es obligatorio',
                          pattern: { value: EMAIL_PATTERN, message: 'Ingresa un correo válido' },
                        })}
                      />
                    </FormField>
                  </div>

                  <div className="grid gap-6 md:grid-cols-2">
                    <FormField label="Teléfono" error={errors.telefono?.message} variant="light" size="lg">
                      <input
                        className={fieldClass(inputLight, Boolean(errors.telefono))}
                        type="tel"
                        autoComplete="tel"
                        aria-invalid={Boolean(errors.telefono)}
                        {...register('telefono', {
                          validate: (v) =>
                            !v || String(v).replace(/\D/g, '').length >= 7 || 'Teléfono inválido (mín. 7 dígitos)',
                        })}
                      />
                    </FormField>
                    <FormField
                      label="Tipo de PQRS"
                      error={errors.tipoPqrs?.message}
                      variant="light"
                      size="lg"
                      required
                    >
                      <select
                        className={fieldClass(selectLight, Boolean(errors.tipoPqrs))}
                        aria-invalid={Boolean(errors.tipoPqrs)}
                        {...register('tipoPqrs', {
                          required: 'Selecciona el tipo de solicitud',
                          validate: (v) => (v && v !== '') || 'Selecciona el tipo de solicitud',
                        })}
                      >
                        <option value="" disabled>
                          Seleccione una opción
                        </option>
                        {PQRS_TYPES.map((tipo) => (
                          <option key={tipo.value} value={tipo.value}>
                            {tipo.label}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  </div>

                  {tipoInfo?.description ? (
                    <p className="rounded-lg border border-[#1acb12]/25 bg-[#1acb12]/8 px-4 py-3 text-base leading-relaxed text-slate-600">
                      <span className="font-semibold text-[#0d2b7f]">{tipoInfo.label}:</span> {tipoInfo.description}
                    </p>
                  ) : null}

                  <FormField label="Mensaje" error={errors.mensaje?.message} variant="light" size="lg" required>
                    <textarea
                      rows={5}
                      className={fieldClass(inputLight, Boolean(errors.mensaje))}
                      placeholder="Describe tu solicitud con el mayor detalle posible…"
                      aria-invalid={Boolean(errors.mensaje)}
                      {...register('mensaje', {
                        required: 'El mensaje es obligatorio',
                        minLength: { value: 10, message: 'Escribe al menos 10 caracteres' },
                        maxLength: { value: 2000, message: 'Máximo 2000 caracteres' },
                      })}
                    />
                  </FormField>

                  {!captchaLoading ? (
                    <SumCaptcha
                      mode="server"
                      size="lg"
                      serverChallenge={captchaChallenge}
                      onRefresh={loadCaptcha}
                      value={captchaAnswer}
                      onChange={setCaptchaAnswer}
                      onValidChange={setCaptchaValid}
                    />
                  ) : (
                    <p className="text-base text-slate-500">Cargando verificación de seguridad…</p>
                  )}

                  <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                    <Button
                      type="submit"
                      disabled={isSubmitting || !captchaValid || captchaLoading}
                      className="w-full text-base sm:w-auto"
                    >
                      {isSubmitting ? 'Enviando…' : 'Enviar PQRS'}
                    </Button>
                    {whatsappHref ? (
                      <a
                        href={whatsappHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#25D366]/50 bg-[#25D366] px-5 py-2.5 text-base font-semibold text-white shadow-sm transition hover:bg-[#20bd5a] sm:w-auto"
                      >
                        <MessageCircle className="h-4 w-4" aria-hidden />
                        {publicCta.contactWhatsAppLabel}
                        <ExternalLink className="h-4 w-4 opacity-80" aria-hidden />
                      </a>
                    ) : null}
                  </div>
                </form>
              </div>
            </ScrollReveal>

            <div className="space-y-8">
              <ScrollReveal delayMs={80}>
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.28em] text-[#00A63E]">Datos de contacto</p>
                  <h2 className="mt-3 font-display text-2xl font-bold text-[#0d2b7f] md:text-3xl">Ubicación y canales</h2>
                  <p className="mt-2 text-base leading-relaxed text-slate-600">
                    Visítanos en nuestra sede en Bogotá o escríbenos por WhatsApp, teléfono y correo institucional.
                  </p>
                </div>

                <ul className="mt-6 space-y-3">
                  <ContactInfoItem icon={MapPin} label="Dirección">
                    <a
                      href={OFFICE_MAPS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#0d2b7f] transition hover:text-[#00A63E] hover:underline"
                    >
                      {OFFICE_LOCATION_LABEL}
                    </a>
                  </ContactInfoItem>
                  <ContactInfoItem icon={Phone} label="Teléfono">
                    <a href={phoneHref} className="text-[#0d2b7f] transition hover:text-[#00A63E] hover:underline">
                      {OFFICE_PHONE_DISPLAY}
                    </a>
                  </ContactInfoItem>
                  {DEPARTMENT_CONTACT_LINES.map((line) => (
                    <ContactInfoItem key={line.telDigits} icon={MessageCircle} label={line.label}>
                      <a
                        href={getDepartmentWhatsAppHref(line)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex flex-col gap-0.5 text-[#0d2b7f] transition hover:text-[#00A63E] sm:flex-row sm:items-center sm:gap-2"
                        aria-label={`${line.label} por WhatsApp`}
                      >
                        <span className="hover:underline">{line.display}</span>
                        <span className="text-sm font-semibold text-[#25D366] sm:text-base">WhatsApp</span>
                      </a>
                    </ContactInfoItem>
                  ))}
                  {whatsappHref ? (
                    <ContactInfoItem icon={MessageCircle} label="WhatsApp">
                      <a
                        href={whatsappHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex flex-col gap-0.5 text-[#0d2b7f] transition hover:text-[#00A63E] sm:flex-row sm:items-center sm:gap-2"
                      >
                        <span className="hover:underline">{formatPhoneDisplay()}</span>
                        <span className="text-sm font-semibold text-[#25D366] sm:text-base">
                          {publicCta.contactWhatsAppLabel}
                        </span>
                      </a>
                    </ContactInfoItem>
                  ) : null}
                  <ContactInfoItem icon={Mail} label="Correo">
                    <a
                      href={`mailto:${OFFICE_EMAIL}`}
                      className="break-all text-[#0d2b7f] transition hover:text-[#00A63E] hover:underline"
                    >
                      {OFFICE_EMAIL}
                    </a>
                  </ContactInfoItem>
                </ul>
              </ScrollReveal>

              <ScrollReveal delayMs={140}>
                <div>
                  <h3 className="font-display text-xl font-semibold text-slate-800 md:text-2xl">Cómo llegar</h3>
                  <p className="mt-1 text-base text-slate-500">Cra. 58 #5C-25, Bogotá, Colombia</p>
                </div>
                <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm ring-1 ring-slate-100">
                  <div className="aspect-[4/3] w-full min-h-[280px] sm:min-h-[320px] lg:min-h-[360px]">
                    <iframe
                      src={OFFICE_MAPS_EMBED_URL}
                      title="Ubicación de Coonaltragas en Google Maps"
                      className="h-full w-full border-0"
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                </div>
                <a
                  href={OFFICE_MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex text-base font-semibold text-[#0d2b7f] transition hover:text-[#00A63E] hover:underline"
                >
                  Abrir en Google Maps
                </a>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
